import { useEffect, useMemo, useState } from 'react'
import { DatePickerButton, DateRangeProps } from 'common/components/DatePickerButton'
import { useParallelApiCalls, UseParallelApiCalls, UseParallelApiCallsChunkStats } from '../useParallelApiCalls'
import { generateChunks } from '../js/generateChunks'
import {
  DAY,
  PARALLEL_CALL_OPTIONS,
  machineIdFromUrl,
  toTimestamp,
  formatSeconds,
  formatDate,
  formatClock
} from './js/demoHelpers'
import { Gauge } from './Gauge'
import { LoadChart, LoadChartSettings } from './LoadChart'
import { chunkRows } from './js/chunkRows'
import './css/Demo.css'

const DEMO_APIS: Record<string, { path: string; args: Record<string, string[]>; chart: LoadChartSettings }> = {
  'pump-stroke-events': {
    path: '/mh/v1/dsi/machines/{machine_id}/pump-stroke-events',
    args: {
      aggregation_type: ['Hours', 'HOUR_6', '30_Minutes', '15_Minutes', 'None']
    },
    chart: {
      axes: {
        bottom: { value_key: 'timestamp', scale_type: 'time', label: 'Time (UTC)' },
        left: { value_key: 'total', scale_type: 'linear', label: 'Total SPM' }
      },
      toRows: chunkRows
    }
  },
  'dsi-kpi throughput-rate': {
    path: '/mh/v2/dsi/machine-health/{machine_id}/throughput-rate',
    args: {
      kpi_type: ['Hours', '30_Minutes', '15_Minutes', 'Minutes', 'Aggregate']
    },
    chart: {
      axes: {
        bottom: { value_key: 'timestamp', scale_type: 'time', label: 'Time (UTC)' },
        left: { value_key: 'value', scale_type: 'linear', label: 'Throughput rate' }
      }
    }
  },
  'dsi-kpi processed-product-percentage': {
    path: '/mh/v2/dsi/machine-health/{machine_id}/processed-product-percentage',
    args: {
      kpi_type: ['Hours', '30_Minutes', '15_Minutes', 'Minutes', 'Aggregate']
    },
    chart: {
      axes: {
        bottom: { value_key: 'timestamp', scale_type: 'time', label: 'Time (UTC)' },
        left: { value_key: 'value', scale_type: 'linear', label: 'Processed product %' }
      }
    }
  }
}

const DEMO_MACHINE_IDS = ['a21e5327-3c68-469e-9a44-494172fe233f']

const firstArgValues = (api_name: string): Record<string, string> =>
  Object.fromEntries(Object.entries(DEMO_APIS[api_name]?.args || {}).map(([key, options]) => [key, options[0]]))

export const Demo = (): JSX.Element => {

  const [api_name, setApiName] = useState(Object.keys(DEMO_APIS)[0])
  const [arg_values, setArgValues] = useState(() => firstArgValues(Object.keys(DEMO_APIS)[0]))
  const [machine_id, setMachineId] = useState(machineIdFromUrl() || DEMO_MACHINE_IDS[0])
  const [date_range, setDateRange] = useState(() => {
    const end_time = Date.now()
    return { startTime: end_time - 60 * DAY, endTime: end_time }
  })
  const [chunk_days, setChunkDays] = useState(5)
  const [progressive_loading, setProgressiveLoading] = useState(false)
  const [parallel_call_max, setParallelCallMax] = useState<UseParallelApiCalls['parallel_call_max']>(4)
  const [settings, setSettings] = useState<UseParallelApiCalls>({ parallel_call_max: 4, api_path: '' })
  const [run_api_name, setRunApiName] = useState(api_name)

  const { data, stats } = useParallelApiCalls(settings)

  const [now, setNow] = useState(Date.now())
  const run_start = stats.load_start
  const is_finished = stats.load_end !== undefined

  useEffect(() => {
    if (run_start === undefined || is_finished) return
    const timer = window.setInterval(() => setNow(Date.now()), 100)
    return () => window.clearInterval(timer)
  }, [run_start, is_finished])

  const elapsedOf = (load_end?: number): number =>
    run_start === undefined ? 0 : Math.max(0, (load_end ?? now) - run_start)

  const gauge_max = elapsedOf(is_finished ? stats.load_end : undefined)

  const run_keys = useMemo(
    () => new Set(stats.chunks.map(chunk => `${chunk.start_time}-${chunk.end_time}`)),
    [stats.chunks]
  )

  const chunk_rows = useMemo((): UseParallelApiCallsChunkStats[] => {
    const preview_chunks = generateChunks({
      start_time: date_range.startTime,
      end_time: date_range.endTime,
      chunk_time: chunk_days * DAY,
      progressive_loading
    })
    return preview_chunks.map(chunk =>
      stats.chunks.find(run_chunk => run_chunk.start_time === chunk.start_time && run_chunk.end_time === chunk.end_time)
        || { ...chunk, status: 'waiting', rows: 0 }
    )
  }, [date_range, chunk_days, progressive_loading, stats.chunks])

  const handleDateSubmit = (range?: DateRangeProps): void => {
    const start_time = toTimestamp(range?.startTime)
    const end_time = toTimestamp(range?.endTime)
    if (start_time === undefined || end_time === undefined) return
    setDateRange({ startTime: start_time, endTime: end_time })
  }

  const handleApiChange = (next_api_name: string): void => {
    setApiName(next_api_name)
    setArgValues(firstArgValues(next_api_name))
  }

  const handleRun = (): void => {
    setRunApiName(api_name)
    setSettings({
      start_time: date_range.startTime,
      end_time: date_range.endTime,
      chunk_time: chunk_days * DAY,
      progressive_loading,
      parallel_call_max,
      api_path: DEMO_APIS[api_name].path.replace('{machine_id}', machine_id),
      api_args: Object.fromEntries(Object.entries(arg_values).filter(([, value]) => value && value !== 'None'))
    })
  }

  const done_count = chunk_rows.filter(chunk => chunk.status === 'done').length
  const error_count = chunk_rows.filter(chunk => chunk.status === 'error').length

  return (
    <div className='demo-page demo-stack'>
      <h1 className='demo-heading'>useParallelApiCalls</h1>

      <div className='demo-box demo-skin-surface demo-row'>
        <label className='demo-field'>
          <span className='demo-field__label demo-skin-muted'>api</span>
          <select className='demo-field__input demo-skin-input' value={api_name} onChange={event => handleApiChange(event.target.value)}>
            {Object.keys(DEMO_APIS).map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
        <label className='demo-field'>
          <span className='demo-field__label demo-skin-muted'>machine_id</span>
          <input
            className='demo-field__input demo-skin-input'
            list='demo-machine-ids'
            value={machine_id}
            onChange={event => setMachineId(event.target.value)}
          />
          <datalist id='demo-machine-ids'>
            {DEMO_MACHINE_IDS.map(id => <option key={id} value={id} />)}
          </datalist>
        </label>
        <label className='demo-field'>
          <span className='demo-field__label demo-skin-muted'>chunk days</span>
          <input
            className='demo-field__input demo-skin-input'
            type={progressive_loading ? 'text' : 'number'}
            min={1}
            value={progressive_loading ? '-' : chunk_days}
            disabled={progressive_loading}
            onChange={event => setChunkDays(Number(event.target.value))}
          />
        </label>
        <label className='demo-field'>
          <span className='demo-field__label demo-skin-muted'>progressive_loading</span>
          <input type='checkbox' checked={progressive_loading} onChange={event => setProgressiveLoading(event.target.checked)} />
        </label>
        <label className='demo-field'>
          <span className='demo-field__label demo-skin-muted'>parallel_call_max</span>
          <select
            className='demo-field__input demo-skin-input'
            value={progressive_loading ? 4 : parallel_call_max}
            disabled={progressive_loading}
            onChange={event => setParallelCallMax(Number(event.target.value) as UseParallelApiCalls['parallel_call_max'])}
          >
            {PARALLEL_CALL_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        {Object.entries(DEMO_APIS[api_name]?.args || {}).map(([key, options]) => (
          <label key={key} className='demo-field'>
            <span className='demo-field__label demo-skin-muted'>{key}</span>
            <select
              className='demo-field__input demo-skin-input'
              value={arg_values[key]}
              onChange={event => setArgValues(previous => ({ ...previous, [key]: event.target.value }))}
            >
              {options.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
        ))}
        <div className='demo-field'>
          <span className='demo-field__label demo-skin-muted'>{formatDate(date_range.startTime)}</span>
          <span className='demo-field__label demo-skin-muted'>{formatDate(date_range.endTime)}</span>
        </div>
        <div className='demo-field'>
          <span className='demo-field__label demo-skin-muted'>start / end time</span>
          <DatePickerButton
            timeRange
            startTime={date_range.startTime}
            endTime={date_range.endTime}
            handleSubmit={handleDateSubmit}
          />
        </div>
        <button className='demo-btn demo-skin-primary' disabled={!machine_id} onClick={handleRun}>Run</button>
      </div>

      <div className='demo-box demo-skin-surface demo-row'>
        <div className='demo-stat'>
          <span className='demo-skin-muted'>total load time</span>
          <span className='demo-stat__value'>{formatSeconds(stats.load_time)}</span>
        </div>
        <div className='demo-stat'>
          <span className='demo-skin-muted'>timeframe</span>
          <span className='demo-stat__value demo-stat__value--strong'>
            {Number(((date_range.endTime - date_range.startTime) / DAY).toFixed(1))} Days
          </span>
        </div>
        <div className='demo-stat'>
          <span className='demo-skin-muted'>chunks done</span>
          <span className='demo-stat__value'>{done_count} / {chunk_rows.length}</span>
        </div>
        <div className='demo-stat'>
          <span className='demo-skin-muted'>errors</span>
          <span className='demo-stat__value'>{error_count}</span>
        </div>
        <div className='demo-stat'>
          <span className='demo-skin-muted'>rows</span>
          <span className='demo-stat__value'>{stats.chunks.reduce((sum, chunk) => sum + chunk.rows, 0)}</span>
        </div>
      </div>

      <div className='demo-box demo-skin-surface'>
        <table className='demo-table'>
          <thead>
            <tr className='demo-skin-line demo-skin-muted'>
              <th className='demo-table__cell demo-table__cell--num'>#</th>
              <th className='demo-table__cell'>chunk start</th>
              <th className='demo-table__cell'>chunk end</th>
              <th className='demo-table__cell demo-table__cell--num'>days</th>
              <th className='demo-table__cell'>status</th>
              <th className='demo-table__cell demo-table__cell--num'>rows</th>
              <th className='demo-table__cell'>call start</th>
              <th className='demo-table__cell'>call end</th>
              <th className='demo-table__cell demo-table__cell--num'>load_time</th>
            </tr>
          </thead>
          <tbody>
            {chunk_rows.map((chunk, index) => (
              <tr key={chunk.start_time} className='demo-skin-line'>
                <td className='demo-table__cell demo-table__cell--num'>{index + 1}</td>
                <td className='demo-table__cell'>{formatDate(chunk.start_time)}</td>
                <td className='demo-table__cell'>{formatDate(chunk.end_time)}</td>
                <td className='demo-table__cell demo-table__cell--num'>{Number(((chunk.end_time - chunk.start_time) / DAY).toFixed(2))}</td>
                <td className={`demo-table__cell demo-skin-${chunk.status}`}>{chunk.status}</td>
                <td className='demo-table__cell demo-table__cell--num'>{chunk.rows}</td>
                <td className='demo-table__cell'>{formatClock(chunk.load_start)}</td>
                <td className='demo-table__cell'>{formatClock(chunk.load_end)}</td>
                <td className='demo-table__cell demo-table__cell--num'>{formatSeconds(chunk.load_time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='demo-box demo-skin-surface demo-grid'>
        {chunk_rows.map((chunk, index) => (
          <div key={chunk.start_time} className={`demo-skin-${chunk.status}`}>
            <div className='demo-frame'>
              <Gauge
                start={run_start !== undefined && run_keys.has(`${chunk.start_time}-${chunk.end_time}`)}
                end={chunk.load_end !== undefined}
                value={elapsedOf(chunk.load_end)}
                max={gauge_max}
                rows={chunk.rows}
                label={`#${index + 1} · ${Number(((chunk.end_time - chunk.start_time) / DAY).toFixed(2))}d · ${chunk.status}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className='demo-box demo-skin-surface'>
        <LoadChart
          {...DEMO_APIS[stats.chunks.length ? run_api_name : api_name]?.chart}
          data={data}
          chunks={stats.chunks.length ? stats.chunks : chunk_rows}
          start_time={stats.chunks.length && settings.start_time !== undefined ? settings.start_time : date_range.startTime}
          end_time={stats.chunks.length && settings.end_time !== undefined ? settings.end_time : date_range.endTime}
        />
      </div>
    </div>
  )
}
